/**
 * Double-submit CSRF helper.
 *
 * The backend issues a `fias_csrf` cookie alongside every successful
 * login / refresh. The cookie is intentionally *not* httpOnly so we
 * can read it here and echo the value as `X-CSRF-Token` on every
 * state-changing request. The backend compares header against cookie
 * in constant time and rejects a mismatch with 403.
 *
 * Why double-submit instead of relying on SameSite alone:
 *   The platform deploys cross-origin (frontend on its own domain,
 *   API on another). That forces SameSite=None on auth cookies, which
 *   disables the browser's built-in CSRF protection. The custom-header
 *   approach plugs the gap — a hostile origin cannot read our CSRF
 *   cookie (different origin) and cannot send a custom header on a
 *   cross-origin form submission without a CORS preflight, which our
 *   allowlist rejects.
 *
 * GET / HEAD / OPTIONS skip CSRF (they're idempotent). The auth
 * endpoints that bootstrap a session (login, signup, refresh) also
 * skip it server-side because the user has no cookie yet.
 */

const CSRF_COOKIE = 'fias_csrf';

/**
 * Cached CSRF value, used only as a FALLBACK for when the cookie can't
 * be read (SSR / tests / the brief window after login before the
 * Set-Cookie is observable). Login / refresh / dev-login responses
 * include `csrfToken` in their JSON body, which we capture via
 * `setCsrfToken` to populate this fallback.
 *
 * It is deliberately NOT the primary source: the `fias_csrf` cookie is.
 * See `getCsrfToken` for why reading the cookie fresh each call is
 * required for the double-submit contract to hold.
 */
let cachedToken: string | null = null;

function readCookie(): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const prefix = `${CSRF_COOKIE}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

export function setCsrfToken(token: string | null): void {
  cachedToken = token;
}

export function getCsrfToken(): string | null {
  // Read the cookie FRESH on every call. The double-submit check on the
  // server compares our `X-CSRF-Token` header against the `fias_csrf`
  // cookie the browser sends with the SAME request — so the header must
  // equal the *current* cookie value, not a value we cached earlier.
  //
  // The cookie can rotate out-of-band during a long-lived session: a
  // token refresh (in this tab or another tab sharing the cookie) or a
  // server-side re-issue swaps `fias_csrf` for a new value via
  // Set-Cookie. A cache-first lookup would keep echoing the stale token,
  // producing an intermittent 403 "CSRF token missing or invalid" on the
  // next state-changing request — exactly the failure seen on long
  // image-editor sessions. Reading the cookie each time keeps the header
  // in lockstep with whatever the browser will actually send.
  //
  // Fall back to the cached token only when the cookie isn't readable
  // (no `document`, or the post-login window before Set-Cookie lands).
  const cookieValue = readCookie();
  if (cookieValue) {
    cachedToken = cookieValue;
    return cookieValue;
  }
  return cachedToken;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Mutates `headers` to include `X-CSRF-Token` when the request method
 * is unsafe and a CSRF token is available. Safe verbs are left alone
 * — the backend skips CSRF on them anyway, and adding the header
 * would force a CORS preflight on every GET.
 */
export function attachCsrfHeader(method: string, headers: Record<string, string>): void {
  if (SAFE_METHODS.has(method.toUpperCase())) return;
  const token = getCsrfToken();
  if (token) {
    headers['X-CSRF-Token'] = token;
  }
}
